// The error taxonomy. Every application failure in this app is one of these eight classes, so a
// catch block can discriminate with `instanceof` and re-throw what it did not match (INVARIANTS
// rule 17).

export abstract class DomainError extends Error {
  abstract readonly tag: string;

  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    // Without this, every subclass instance logs as "Error" because Error captures name eagerly.
    this.name = this.constructor.name;
  }
}

// A write or read that failed on the far side: gateway unreachable, 5xx, timeout, a rejected
// commit. The store that made the call catches this, rolls back, and toasts.
export class ServerError extends DomainError {
  readonly tag = 'ServerError' as const;
}

// User input that failed validation. Carries the field so the view can render inline against it.
export class ValidationError extends DomainError {
  readonly tag = 'ValidationError' as const;
  constructor(
    public readonly field: string,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
  }
}

// An OCC clash: the aggregate moved under us. Distinct from ServerError because the response is
// different (re-read and retry with the same idempotency key, not "tell the user it broke").
// docs/sync.md catalogues which events are OCC and which are not.
export class ConflictError extends DomainError {
  readonly tag = 'ConflictError' as const;
}

// The device has no network, so the write was never attempted. Distinct from ServerError because
// the response is different: gate the buttons and say so, rather than telling the user the gateway
// broke, and never enter a retry ladder for a write that was never going anywhere. This app is
// online-only by design (docs/sync.md); the SDK's own OfflineError translates into this one at the
// gateway boundary, and the store gates before it ever gets there.
export class OfflineError extends DomainError {
  readonly tag = 'OfflineError' as const;
}

// The server is emitting events this build cannot fold, so the SDK has taken the aggregate
// read-only rather than let this device compute writes against a stale picture. Distinct from
// ServerError because the response is a reload, not a retry: every tap on this build will fail the
// same way until the page is refreshed onto a newer one.
export class UpdateRequiredError extends DomainError {
  readonly tag = 'UpdateRequiredError' as const;
}

// This device has no grant on the aggregate it just asked about: the share code was never redeemed
// here, it was revoked, or a single-use code had already been burned. Also what a NON-OWNER device
// gets for asking to mint, which is the other half of why this is its own class.
//
// Split out of ServerError because the two want opposite responses and read as opposite things to a
// human. A ServerError says the gateway broke and invites a retry; a retry here will fail forever,
// and nothing is broken — the answer is "that code is not the one for this session", or, on the
// admin side, "this is not the laptop that created the day". Flattening them told a coach holding
// yesterday's QR that the wifi was down.
export class ForbiddenError extends DomainError {
  readonly tag = 'ForbiddenError' as const;
}

export class NotFoundError extends DomainError {
  readonly tag = 'NotFoundError' as const;
  constructor(
    public readonly what: string,
    options?: { cause?: unknown },
  ) {
    super(`Not found: ${what}`, options);
  }
}

// A code invariant was violated. Always a bug, and where it surfaces depends on when it is thrown.
// Raised during render, it reaches ErrorBoundaryView, which is the only thing that catches it.
// Raised at boot before the tree is mounted (assertBootSubscribers is the shipped case), it is
// caught by nothing: the user gets a blank page and the reason is in the console.
export class InvariantError extends DomainError {
  readonly tag = 'InvariantError' as const;
}

// Use instead of `if (!x) throw new Error(...)`. The `asserts` signature narrows the type for
// TypeScript after the call, so the caller does not need a redundant non-null assertion.
export function invariant(condition: unknown, message: string): asserts condition {
  if (!condition) throw new InvariantError(message);
}

// Opt-in, for the rare function whose multiple failure kinds are part of its contract (a parser,
// a multi-field validator). `throw` is the default everywhere else. No .map/.mapErr ceremony on
// purpose: wanting that means you are choosing a different model, which is a longer conversation.
export type Result<T, E> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: E };

export const ok = <T>(value: T): Result<T, never> => ({ ok: true, value });
export const err = <E>(error: E): Result<never, E> => ({ ok: false, error });
